/**
 * Selection feedback shared by every pick option — team, player, yes/no, over/under.
 *
 * Deliberately CSS-only. These components render inside four apps that each own their
 * Tailwind config, so anything built on a custom keyframe would animate in some of them
 * and silently do nothing in the others. A transitioned transform needs no keyframe, and
 * Tailwind's built-in `motion-reduce:` variant covers the accessibility opt-out.
 */
import React, { useCallback, useState } from 'react';

export interface PickFlourishState {
  isSelected: boolean;
  /** Whether ANY option on this question is currently selected. */
  anySelected: boolean;
  /** Results mode. The pick is settled, so the card must keep its graded green/red look. */
  hasResult?: boolean;
  disabled?: boolean;
  /**
   * Whether this option may animate between states. False until the player touches the
   * question — see `usePickInteraction`.
   */
  animate?: boolean;
}

/**
 * Every property the option buttons transition.
 *
 * The colours have to be listed here. The buttons also carry `.transition-smooth`, which
 * is `transition: all 0.2s` declared in `@layer components`; this is a utility, and
 * utilities are emitted after components, so whatever it names replaces that `all`.
 * Leaving the colours out made the background, border and text snap on every pick.
 */
const TRANSITIONED_PROPERTIES =
  'transition-[transform,opacity,filter,background-color,border-color,color]';

/**
 * Classes for an option button: the chosen option lifts, its siblings recede.
 *
 * Returns an empty string once a question is graded — the correct/incorrect border and
 * glow are the message at that point, and shrinking the losing option would fight it.
 *
 * Under reduced motion the state styling still applies, it simply arrives with no
 * transition and no transform. That is the right reading of the preference: a static
 * change of emphasis is not motion. Note that nothing here may override `opacity`
 * unconditionally — a disabled option carries its own `opacity-90` dim.
 */
export function pickFlourishClass({
  isSelected,
  anySelected,
  hasResult = false,
  disabled = false,
  animate = true,
}: PickFlourishState): string {
  if (hasResult) return '';

  // Before the player has touched the question, the styling still applies but the lift
  // does not animate: a restored pick must render already lifted rather than flinch into
  // place once the saved selections arrive.
  //
  // This still has to name the colour properties, and cannot simply omit a transition
  // utility. `.transition-smooth` on these buttons is `transition: all`, which covers
  // `transform` too — left to it, the lift would animate on hydration after all. Naming
  // the colours alone keeps their fade (the behaviour these buttons already had, hover
  // included) while the transform lands instantly.
  const base = animate
    ? `transform-gpu ${TRANSITIONED_PROPERTIES} duration-300 ease-out motion-reduce:transform-none motion-reduce:transition-none`
    : 'transform-gpu transition-[background-color,border-color,color] duration-200 ease-in-out';

  if (isSelected) return `${base} scale-[1.03] z-10`;

  // Only the unchosen siblings recede, and only once a choice exists — an untouched
  // question must show every option at full strength.
  if (anySelected && !disabled) return `${base} scale-[0.97] opacity-60 saturate-50`;

  return `${base} active:scale-[0.97]`;
}

/**
 * A light sweep that crosses the option as it is chosen.
 *
 * Render it as the LAST child of the (relative, overflow-hidden) option button so it
 * passes over the artwork and label rather than under them.
 *
 * The sweep is a transition, not an animation: it parks off the left edge while
 * unselected and off the right edge while selected, so selecting runs it left-to-right
 * and deselecting runs it back.
 *
 * `animate` is what keeps a restored pick quiet. Parked resting states are not enough on
 * their own — saved selections arrive in an effect after the cards have already painted
 * empty, so without this a player returning to a contest they had filled in would see
 * every card sweep at once.
 */
export const PickSweep: React.FC<{ active: boolean; animate?: boolean }> = ({
  active,
  animate = true,
}) => (
  <span
    aria-hidden="true"
    data-testid="pick-sweep"
    className={`pointer-events-none absolute inset-0 ${
      animate ? 'transition-transform duration-[600ms] ease-out motion-reduce:hidden' : ''
    } ${active ? 'translate-x-full' : '-translate-x-full'}`}
    style={{
      background:
        'linear-gradient(105deg, transparent 30%, rgba(83, 211, 55, 0.45) 50%, transparent 70%)',
    }}
  />
);

/**
 * Tracks whether the player has touched this question yet.
 *
 * Only a change the player made here should animate. A selection restored from the server
 * arrives as an ordinary prop change long after mount, and is indistinguishable from a tap
 * unless the component records the tap itself.
 *
 * Armed on pointer-down rather than on click so that the transition classes are committed
 * in an earlier render than the selection change — added in the same frame as the value
 * they animate, a browser would skip the transition, and the first pick of each question
 * would snap. Keyboard activation has no pointer-down, so it arms on click instead and
 * only its very first activation lands without the flourish.
 */
export function usePickInteraction() {
  const [hasInteracted, setHasInteracted] = useState(false);
  const arm = useCallback(() => setHasInteracted(true), []);

  return { hasInteracted, interactionProps: { onPointerDown: arm }, arm };
}
